import React, { ReactNode, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Svg from '../Svg';
import Icon from '../Icon';
import IconButton from '../IconButton';
import Dropdown from '../Dropdown';
import HeaderPatientInfo from '../HeaderPatientInfo';
import { PatientInfoVisibility } from '../../types/PatientInfoVisibility';
import { useTheme, useModal } from '../../contextProviders';
import { useLocation, useNavigate } from 'react-router-dom';
import TopNavBar from '../TopNavBar';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getAIStudiesApi from '@custom/rtk-apis/appointment/getAIStudies';

function TopBar({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  showPatientInfo = PatientInfoVisibility.VISIBLE_COLLAPSED,
  servicesManager,
  Secondary,
  appConfig,
  reportsOptions = null,
  ...props
}: withAppTypes): ReactNode {
  const { t } = useTranslation('Header');
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { show, hide } = useModal();
  const dispatch = useDispatch<AppDispatch>();

  // Parse StudyInstanceUIDs from URL query params
  const params = new URLSearchParams(location.search);
  const uidParam = params.get('StudyInstanceUIDs') || params.get('StudyInstanceUID') || '';
  const studyUIDs = uidParam ? uidParam.split(',') : [];

  // Read AI studies from Redux and fetch if missing
  const { result: aiStudyResult, status: aiStudyFetchStatus } = useSelector((state: RootState) => state.getAIStudiesResponse);

  useEffect(() => {
    if (!aiStudyResult && !aiStudyFetchStatus) {
      const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
      const hospitalCode = sessionStorage.getItem('hospitalCode') || localStorage.getItem('hospitalCode');
      if (hospitalId && hospitalCode) {
        dispatch(getAIStudiesApi({ hospitalId, hospitalCode } as any));
      }
    }
  }, [aiStudyResult, aiStudyFetchStatus, dispatch]);

  const aiStudiesFlat = Array.isArray(aiStudyResult)
    ? aiStudyResult.flatMap((d: any) => d?.studies || [])
    : [];
  const matchedEntries = studyUIDs
    .map(uid => aiStudiesFlat.find((s: any) => s?.studyInstanceUID === uid))
    .filter(Boolean);
  const completedEntry = matchedEntries.find((e: any) => (e?.status || '').toLowerCase() === 'completed');
  const showAiReportButton = !!completedEntry;

  // Modal content for AI report
  const AIReportModal: React.FC<{ entry: any }> = ({ entry }) => {
    if (!entry) return null;
    const report = entry?.aiResult?.report;
    const summaryResults = entry?.aiResult?.summary_results || {};
    const modality = entry?.modality || 'N/A';
    const studyRegion = entry?.studyRegion || 'N/A';

    const renderInline = (text: string): React.ReactNode[] => {
      const nodes: React.ReactNode[] = [];
      if (!text) return nodes;
      // First handle bold **text**
      const boldSplit = text.split(/(\*\*[^*]+\*\*)/g);
      boldSplit.forEach((seg, i) => {
        if (/^\*\*[^*]+\*\*$/.test(seg)) {
          const inner = seg.slice(2, -2);
          // Within bold, allow italics *text*
          nodes.push(<strong key={`b-${i}`}>{renderItalics(inner, `bi-${i}`)}</strong>);
        } else {
          // Normal segment, process italics
          nodes.push(...renderItalics(seg, `i-${i}`));
        }
      });
      return nodes;
    };

    const renderItalics = (text: string, keyPrefix: string): React.ReactNode[] => {
      const pieces: React.ReactNode[] = [];
      const italicSplit = text.split(/(\*[^*]+\*)/g);
      italicSplit.forEach((seg, j) => {
        if (/^\*[^*]+\*$/.test(seg)) {
          pieces.push(<em key={`${keyPrefix}-e-${j}`}>{seg.slice(1, -1)}</em>);
        } else if (seg) {
          pieces.push(<span key={`${keyPrefix}-t-${j}`}>{seg}</span>);
        }
      });
      return pieces;
    };

    const MarkdownRenderer: React.FC<{ text: string }> = ({ text }) => {
      if (!text) return <div className="text-sm">No report available</div>;
      const lines = text.replace(/\r\n/g, '\n').split('\n');
      const blocks: React.ReactNode[] = [];
      let i = 0;
      while (i < lines.length) {
        const line = lines[i];
        // Horizontal rule
        if (line.trim() === '---') {
          blocks.push(<hr key={`hr-${i}`} className="my-2" />);
          i += 1;
          continue;
        }
        // Collect bullet list
        if (/^\s*([*\-])\s+/.test(line)) {
          const items: React.ReactNode[] = [];
          while (i < lines.length && /^\s*([*\-])\s+/.test(lines[i])) {
            const itemText = lines[i].replace(/^\s*([*\-])\s+/, '');
            items.push(<li key={`li-${i}`} className="ml-5 list-disc">{renderInline(itemText)}</li>);
            i += 1;
          }
          blocks.push(<ul key={`ul-${i}`} className="mb-2">{items}</ul>);
          continue;
        }
        // Paragraph (preserve empty lines as spacing)
        if (line.trim().length === 0) {
          blocks.push(<div key={`sp-${i}`} className="h-2" />);
          i += 1;
          continue;
        }
        blocks.push(
          <p key={`p-${i}`} className="mb-2 text-sm">
            {renderInline(line)}
          </p>
        );
        i += 1;
      }
      return <div>{blocks}</div>;
    };

    const [copied, setCopied] = useState(false);
    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        },
        (err) => {
          console.error('Could not copy text: ', err);
        }
      );
    };

    return (
      <div className="p-4">
        <div className={`grid grid-cols-1 md:grid-cols-1 gap-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold">Study Report</h3>
              <button
                onClick={() => copyToClipboard(report)}
                title={copied ? "Copied!" : "Copy to clipboard"}
                disabled={copied}
              >
                <div className='flex items-center'>
                  {copied ? (
                    <Icon name="tickIcon" className="w-5 h-5" />
                  ) : (
                    <Icon name="copyIcon" className="w-5 h-5" />
                  )}
                  <span className="ml-1 text-xs">{copied ? "Copied" : "Copy AI Result"}</span>
                </div>
              </button>
            </div>
            <div className="text-sm">
              <MarkdownRenderer text={report} />
            </div>
          </div>
          {!(modality == 'XRAY' && studyRegion == "CHEST") && <div>
            <h3 className="font-semibold mb-2">Series Summary</h3>
            {Object.keys(summaryResults).length === 0 ? (
              <div className="text-sm">No series summary available</div>
            ) : (
              <div className="space-y-4">
                {Object.entries(summaryResults).map(([seriesUID, summary]: [string, any]) => (
                  <div key={seriesUID} className="border rounded-lg p-4 bg-opacity-50">
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-1">
                        {summary?.series_name || 'Unknown Series'}
                      </div>
                      <div className="text-xs text-gray-500 break-all">
                        SeriesInstanceUID: {seriesUID}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Total Slices
                          </span>
                          <span className="font-semibold text-sm">
                            {summary?.num_slices || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Positive Slices
                          </span>
                          <span className={`font-semibold text-sm ${
                            (summary?.positive_slices || 0) > 0
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {summary?.positive_slices || 0}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Avg Probability
                          </span>
                          <span className="font-mono text-sm font-semibold">
                            {summary?.avg_probability || '0.00%'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Max Probability
                          </span>
                          <span className="font-mono text-sm font-semibold">
                            {summary?.max_probability || '0.00%'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>}
        </div>
      </div>
    );
  };

  const handleOpenAIReport = () => {
    if (!completedEntry) return;
    show({
      content: AIReportModal,
      contentProps: { entry: completedEntry },
      title: 'AI Report',
      shouldCloseOnEsc: true,
      shouldCloseOnOverlayClick: true,
      containerDimensions: 'max-w-5xl',
    });
  };

  const handleIsBackFunctionality = () => {
    navigate(`/studyresearch?refetch=true`);
  }

  // TODO: this should be passed in as a prop instead and the react-router-dom
  // dependency should be dropped
  const onClickReturn = () => {
    if (isReturnEnabled) {
      // onClickReturnButton();
      navigate(-1);
      // handleIsBackFunctionality();
    }
  };

  return (
    <TopNavBar
      isSticky={isSticky}
      {...props}
    >
      <div className="relative items-center ">
        <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center">
          <div
            className={classNames(
              'mr-3 inline-flex items-center',
              isReturnEnabled && 'cursor-pointer'
            )}
            onClick={onClickReturn}
            data-cy="return-to-work-list"
          >
            {isReturnEnabled && (
              <Icon
                name="chevron-left"
                className="text-primary-active w-8"
              />
            )}
            <div className="ml-1">
              {/* {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Svg name="logo-ohif" />} */}
            </div>
          </div>
        </div>
        <div className="absolute top-1/2 left-[250px]  h-8 -translate-y-1/2">{Secondary}</div>
        <div className="flex items-center justify-center space-x-2">{children}</div>
        <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center ">
          {reportsOptions !== null &&
            <div className='text-white p-1 rounded mr-2 border border-primary-light'>
              <Dropdown
                id="Reports-Dropdown"
                list={reportsOptions}
                alignment='right'
              >
                Generate Reports
              </Dropdown>
            </div>
          }
          {showAiReportButton && (
            <div className={`${isDarkMode ? 'text-white border-white' : 'text-black border-black'} p-1 rounded mr-4 border`}>
              <button className="px-2 text-sm" onClick={handleOpenAIReport}>
                View AI Report
              </button>
            </div>
          )}
          {showPatientInfo !== PatientInfoVisibility.DISABLED && (
            <HeaderPatientInfo
              servicesManager={servicesManager}
              appConfig={appConfig}
            />
          )}
          <div className="customThemeToggle">
            <label className="inline-flex items-center cursor-pointer mt-0" style={{ marginRight: "5px" }}>
              <input type="checkbox" value="" checked={isDarkMode} className="sr-only peer" onChange={toggleTheme} />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 darkLightMode"></div>
              {/* <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Dark Mode</span> */}
            </label>
          </div>
          <div className="border-primary-dark mx-1.5 h-[25px] border-r"></div>
          <div className="flex-shrink-0">
            <Dropdown
              id="options"
              showDropdownIcon={false}
              list={menuOptions}
              alignment="right"
            >
              <IconButton
                id={'options-settings-icon'}
                variant="text"
                color="inherit"
                size="initial"
                className="text-primary-active hover:bg-primary-dark h-full w-full"
              >
                <Icon name="icon-settings" />
              </IconButton>
            </Dropdown>
          </div>
        </div>
      </div>
    </TopNavBar>
  );
}

TopBar.propTypes = {
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      onClick: PropTypes.func.isRequired,
    })
  ),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isReturnEnabled: PropTypes.bool,
  isSticky: PropTypes.bool,
  // onClickReturnButton: PropTypes.func,
  WhiteLabeling: PropTypes.object,
  showPatientInfo: PropTypes.string,
  servicesManager: PropTypes.object,
};

export default TopBar;
